import React, { useRef, useState, useEffect } from 'react';
import { useDispatch, useSelector, useStore } from 'react-redux';
import { Modal } from '@mui/material';
import { KeyboardArrowDown } from '@mui/icons-material';
import TrashIcon from '@rsuite/icons/Trash';
import VisibleIcon from '@rsuite/icons/Visible';
import CloseIcon from '@rsuite/icons/Close';
import { toggleModal } from '../../../reducers/modalReducer';
import { setFileReport, setCurrentScore, setLogList, setRModalData, setOpenRModal, updateFileAllocation } from '../../../reducers/filesAllocationReducer';
import socket, { postRequest, showMessage, getRequest } from '../../../config';
import AutoFillStepper from '../../autoFill/AutoFillStepper';
import Send from '../../../../images/dataRepo/send.svg';
import Search from '../../../../images/dataRepo/search.svg';
import { Divider } from '@mui/material';

const btnCancel = { background: 'none', border: '1.5px solid #9CA3AF', borderRadius: '6px', cursor: 'pointer', color: '#6B7280', fontSize: '13px', fontWeight: 500, padding: '0.3rem 1.2rem' };
const btnMinimize = { background: 'none', border: '1.5px solid #3B82F6', borderRadius: '6px', cursor: 'pointer', color: '#3B82F6', fontSize: '13px', fontWeight: 500, padding: '0.3rem 1.2rem' };

const TempAutoFillStepperModal = () => {
    const dispatch = useDispatch();
    const store = useStore();
    const modal = useSelector(state => state.modal);
    const { fileReport, currentScore, tab, autoFillContext, company: reduxCompany, tempModule } = useSelector(state => state.fileAllocation);
    const { selectedCompany: company } = useSelector(state => state.company);
    const [indexingProgress, setIndexingProgress] = useState({ total: 0, current: 0, currentFile: '' });
    const [allocationProgress, setAllocationProgress] = useState({ total: 0, current: 0, fileName: '', matchedQNo: '', matchedQuestion: '' });
    const [savingProgress, setSavingProgress] = useState({ total: 0, current: 0, fileName: '', qNo: '' });

    // File preview state (allocation step)
    const [previewFile, setPreviewFile] = useState(null);

    // Hidden file input ref (select step)
    const fileInputRef = useRef(null);

    const phase = modal.autoFillStepperPhase || '';
    const isOpen = modal.open === 'tempAutoFillStepper';

    // ── Socket listeners for indexing & allocation progress ──
    useEffect(() => {
        const onIndexingProgress = (data) => {
            setIndexingProgress({ total: data.total || 0, current: data.current || 0, currentFile: data.currentFile || '' });
        };
        const onAllocationProgress = (data) => {
            setAllocationProgress({ total: data.total || 0, current: data.current || 0, fileName: data.fileName || '', matchedQNo: data.matchedQNo || '', matchedQuestion: data.matchedQuestion || '' });
        };
        const onAllocationCompleted = () => {
            setIndexingProgress({ total: 0, current: 0, currentFile: '' });
            setAllocationProgress({ total: 0, current: 0, fileName: '', matchedQNo: '', matchedQuestion: '' });
        };
        const onSavingProgress = (data) => {
            setSavingProgress({ total: data.total || 0, current: data.current || 0, fileName: data.fileName || '', qNo: data.qNo || '' });
        };
        const onSavingCompleted = () => {
            setSavingProgress({ total: 0, current: 0, fileName: '', qNo: '' });
            // Only close the modal in Filing Assistant mode (no auto-fill)
            const state = store.getState();
            if (!state.modal.autoFillWithFilling) {
                dispatch(toggleModal({ open: '', autoFillStatusBarVisible: false, autoFillStatusBarPhase: '', autoFillJustCompleted: true }));
            }
        };
        socket.on('tempIndexing:progress', onIndexingProgress);
        socket.on('tempAllocation:progress', onAllocationProgress);
        socket.on('tempAllocation:completed', onAllocationCompleted);
        socket.on('tempProcessingFiles:progress', onSavingProgress);
        socket.on('tempProcessingFiles:completed', onSavingCompleted);
        return () => {
            socket.off('tempIndexing:progress', onIndexingProgress);
            socket.off('tempAllocation:progress', onAllocationProgress);
            socket.off('tempAllocation:completed', onAllocationCompleted);
            socket.off('tempProcessingFiles:progress', onSavingProgress);
            socket.off('tempProcessingFiles:completed', onSavingCompleted);
        };
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    // ── Allocation step helpers ──

    const handleFilePreview = (file) => {
        const url = file.location || file.url;
        if (!url) return;
        setPreviewFile({ name: file.originalname || file.title, url });
    };

    const getSectionNameFromSection = (section, qNo) => {
        if (section.sectionNo == qNo || section.no == qNo) {
            const name = section.sectionName || section.name || '';
            return { displayName: name, sectionOnly: name };
        }
        if (section.questions) {
            for (const question of Object.values(section.questions)) {
                if (question.qNo == qNo) {
                    const sectionOnly = section.sectionName || section.name || '';
                    return { displayName: question.qDescription || sectionOnly, sectionOnly };
                }
            }
        }
        if (section.subFolders) {
            for (const subFolder of Object.values(section.subFolders)) {
                const result = getSectionNameFromSection(subFolder, qNo);
                if (result) return result;
            }
        }
        return null;
    };

    const getSectionName = (module, qNo, file, key) => {
        if (qNo == "DXXX" || qNo == "SXXX") return {
            displayName: <div
                onClick={() => handleEditDispatch({ key, fileName: file.originalname, from: 'fileReport', actionType: "move", fileData: file, index: "0" })}
                className='bg62 br10px df aic p1rem jcsb fsi'
            >
                <p>Please select section</p>
                <KeyboardArrowDown className='c3' />
            </div>,
            sectionOnly: ''
        };
        const effectiveCompany = company || reduxCompany;
        // On /company-new/ route, sections live in tempModule (not company.modules[tab])
        let sections;
        if (tempModule?.sections?.length) {
            sections = [...tempModule.sections];
        } else {
            const module2 = module || (effectiveCompany?.modules ? { ...effectiveCompany.modules[tab || "repository"] } : null);
            sections = [...(module2?.sections || [])];
        }
        for (const section of sections) {
            const result = getSectionNameFromSection(section, qNo);
            if (result) return { displayName: `${qNo} ${result.displayName}`, sectionOnly: result.sectionOnly };
        }
        const entry = fileReport?.importResult && Object.values(fileReport.importResult).find(
            r => r.section?.includes(qNo) && r.sectionName
        );
        if (entry?.sectionName) return { displayName: `${qNo} ${entry.sectionName}`, sectionOnly: entry.sectionName };
        return { displayName: "Unknown", sectionOnly: '' };
    };

    const handleEditDispatch = (action) => {
        dispatch(setRModalData({ ...action }));
        dispatch(setOpenRModal(true));
    };

    function isSubarray(subarray, array) {
        const sortedSubarray = subarray.slice().sort();
        const sortedArray = array.slice().sort();
        let i = 0;
        return sortedArray.some((element) => {
            if (element === sortedSubarray[i]) i++;
            return i === sortedSubarray.length;
        });
    }

    const calcAccuracyScore = (count) => {
        let allocated = 0;
        for (const key of Object.keys(fileReport.importResult)) {
            const currentSection = fileReport.importResult[key]['section'];
            const oldSection = fileReport.importResult[key]['originalSection'];
            if (oldSection.includes("DXXX") || oldSection.includes("SXXX")) continue;
            if (isSubarray(oldSection, currentSection)) allocated++;
        }
        const totalCount = count ?? Object.keys(fileReport.importResult).length;
        dispatch(setCurrentScore(!totalCount ? 0 : Math.round(allocated / totalCount * 100)));
        return !totalCount ? 0 : Math.round(allocated / totalCount * 100);
    };

    const removeSectionFromImport = async (fileKey, index) => {
        let sections = [...fileReport.importResult[fileKey]['section']];
        const _fileReport = { ...fileReport };
        const qNo = sections[index];
        sections.splice(index, 1);
        const file = _fileReport.importResult[fileKey]['file'];
        if (sections.length === 0) {
            const { [fileKey]: removedFile, ...restOfFileReport } = _fileReport.importResult;
            _fileReport.importResult = restOfFileReport;
        } else {
            _fileReport.importResult[fileKey] = { ..._fileReport.importResult[fileKey], section: sections };
        }
        const score = calcAccuracyScore(Object.keys(_fileReport.importResult).length);
        const effectiveCompany = company || reduxCompany;
        await postRequest(`/company/deleteSection`, {
            companyId: effectiveCompany._id,
            fileReportId: _fileReport['_id'],
            fileKey, index, qNo, score, file
        });
        dispatch(setFileReport(_fileReport));
    };

    const getImportResultEntryHTML = (key) => {
        if (!fileReport?.importResult?.[key]) return null;
        const fileName = fileReport.importResult[key].file['originalname'];
        const file = fileReport.importResult[key].file;
        const section = fileReport.importResult[key]['section'];
        const effectiveCompany = company || reduxCompany;
        let module = { ...(effectiveCompany?.modules?.[tab || "repository"] || {}) };
        if (module) module = JSON.parse(JSON.stringify(module));
        if (Array.isArray(section)) {
            return <>
                {section.map((qNo, index) => {
                    const { displayName, sectionOnly } = getSectionName(module, qNo, file, key);
                    return <React.Fragment key={`${index + section.length - 1}-section-allocate`}>
                        <tr className={`${index == section.length - 1 ? 'fis-tr' : ''}`}>
                            <td className={`tal fis-td mx1rem fs14px fw600 tdu bg1 lightDot ${index != 0 ? 'vh' : ''} ${qNo == "DXXX" ? 'red' : (fileReport['status'] == 'In Progress' ? 'orange' : 'green')}`}>
                                <span className="cp" onClick={() => handleFilePreview(file)} data-tooltip="Click to preview file">{fileName}</span>
                            </td>
                            <td className={`tal mx1rem fs14px fw400 bg1 c15`}>{sectionOnly ? `${qNo} ${sectionOnly}` : sectionOnly}</td>
                            <td className={`tal mx1rem fs14px fw400 bg1 c15`}>{displayName}</td>
                            {fileReport['status'] == 'In Progress' && (
                                <td className={`mx1rem fs1rem fw600 bg1 tal fis-td fis-td-action`}>
                                    <div className="df fdr jcc aic">
                                        {qNo !== "DXXX" ? <Send data-tooltip={"Move"} className="mr1rem cp" onClick={() => handleEditDispatch({ key, fileName: file.title, from: 'fileReport', actionType: "move", fileData: file, index })} /> : null}
                                        <TrashIcon data-tooltip={"Delete"} onClick={() => removeSectionFromImport(key, index)} className="cp f9 ml1rem" />
                                    </div>
                                </td>
                            )}
                        </tr>
                    </React.Fragment>;
                })}
            </>;
        } else {
            const { displayName, sectionOnly } = getSectionName(module, section, file, key);
            return <>
                <tr>
                    <td className={`tal fis-td mx1rem fs1rem fw600 bg1`}>
                        <span className="cp tdu" onClick={() => handleFilePreview(file)} data-tooltip="Click to preview file">{fileName}</span>
                    </td>
                    <td className={`tal fis-td mx1rem fs1rem fw600 bg1 fis-td`}>{sectionOnly}</td>
                    <td className={`tal fis-td mx1rem fs1rem fw600 bg1 fis-td`}>{displayName}</td>
                    <td className={`mx1rem fs1rem fw600 bg1 tal fis-td fis-td-action`}>
                        <TrashIcon onClick={() => removeSectionFromImport(key, 0)} className="cp f9" />
                    </td>
                </tr>
            </>;
        }
    };

    const onClickReallocationComplete = async () => {
        if (!fileReport) return;
        const fileReportData = { ...fileReport, status: 'Complete', currentScore };
        const moduleName = tab && tab !== "" ? tab : (autoFillContext?.moduleType || '');
        const effectiveCompany = company || reduxCompany;
        const companyId = effectiveCompany?._id || autoFillContext?.companyId;
        const moduleId = autoFillContext?.moduleId;
        if (!moduleId) {
            showMessage('Module is not loaded. Please refresh the page and try again.');
            return;
        }
        dispatch(setFileReport(fileReportData));
        dispatch(toggleModal({ open: 'tempAutoFillStepper', autoFillStepperPhase: 'processing', percent: 0 }));
        const resp = await postRequest(`/modules/allocateFiles`, { companyId, moduleId, fileReport: fileReportData }, true);
        if (!resp || resp.code === 400) return;
        if (resp.body?.report) dispatch(setFileReport(resp.body.report));
        const savedQuestions = resp.body?.savedQuestions;
        if (savedQuestions?.length) {
            dispatch(updateFileAllocation({
                tempModuleAllocationPatch: { moduleId: String(moduleId), savedQuestions },
            }));
        }
        dispatch(toggleModal({ open: '', autoFillStatusBarVisible: false, autoFillStatusBarPhase: '', autoFillJustCompleted: true }));
        showMessage(resp['message']);
    };

    const onClickFileImportAuditLog = async (fileReportId) => {
        const res = await getRequest(`/company/getFileReportLogs/${fileReportId}`);
        dispatch(setLogList(res['body']));
        dispatch(toggleModal({ open: 'fileReportAuditLog' }));
    };

    // ── Handlers ──

    const handleCancel = () => {
        dispatch(toggleModal({ open: '', autoFillStatusBarVisible: false, autoFillStatusBarPhase: '' }));
        showMessage('Import cancelled');
    };

    const handleMinimize = () => {
        const phaseMap = {
            uploading: 'uploading', indexing: 'indexing', allocation: 'allocated',
            processing: 'processingFiles',
        };
        dispatch(toggleModal({ open: '', autoFillStatusBarVisible: true, autoFillStatusBarPhase: phaseMap[phase] || phase }));
    };

    // ── File select step handlers ──
    const cloneFile = (file) => new File([file], file.name, { type: file.type });

    const handleFileSelect = async (e) => {
        const selectedFiles = Array.from(e.target.files);
        if (selectedFiles.length === 0) return;
        const totalFiles = selectedFiles.length;
        let filesInfo = [];
        let relPaths = [];
        try {
            dispatch(toggleModal({
                open: 'tempAutoFillStepper', autoFillStepperPhase: 'uploading',
                percent: 0, fileName: `Uploading ${totalFiles} file${totalFiles > 1 ? 's' : ''}...`, totalFiles, filesUploaded: 0,
                autoFillStatusBarPhase: 'uploading',
                autoFillUploadProgress: { current: 0, total: totalFiles, fileName: '' },
            }));
            relPaths = selectedFiles.map(f => f.webkitRelativePath || '');
            const attachment = selectedFiles.map(f => cloneFile(f));
            const companyId = autoFillContext?.companyId;
            const moduleType = autoFillContext?.moduleType;
            const moduleId = autoFillContext?.moduleId;
            const sectionCode = autoFillContext?.sectionCode;
            const scope2 = autoFillContext?.scope;
            const scopeName2 = autoFillContext?.scopeName || '';

            if (!moduleId) {
                e.target.value = '';
                dispatch(toggleModal({ open: '', autoFillStatusBarVisible: false, autoFillStatusBarPhase: '' }));
                showMessage('Module is not loaded. Please refresh the page and try again.');
                return;
            }

            // Use /modules/uploadFile
            const res = await postRequest('/modules/uploadFile', { attachment, companyId, moduleId, moduleType, totalFiles, sectionCode: sectionCode || '', scope: scope2, scopeName: scopeName2 }, true);
            if (res?.body?.reports?.length) filesInfo = res.body.reports;
            else if (res?.body?.report) filesInfo = [res.body.report];
            e.target.value = '';
            dispatch(toggleModal({ open: 'tempAutoFillStepper', autoFillStepperPhase: 'indexing', autoFillStatusBarPhase: 'indexing', autoFillStatusBarVisible: false }));

            // Use /modules/uploadFiles
            const uploadFilesRes = await postRequest('/modules/uploadFiles', {
                files: filesInfo,
                companyId,
                moduleId,
                tab: moduleType,
                relPaths,
                fromAutoFill: true,
                ...(scope2 === 'section' && sectionCode ? { sectionCode } : {}),
            }, true);
            if (uploadFilesRes?.body?.cancelled) {
                dispatch(toggleModal({ open: '', autoFillStatusBarVisible: false, autoFillStatusBarPhase: '' }));
                return;
            }
            if (!uploadFilesRes?.body?.report) {
                dispatch(toggleModal({ open: '', autoFillStatusBarPhase: 'error' }));
                showMessage('File upload failed');
                return;
            }
            const fileReportResult = uploadFilesRes.body.report;
            const allocated = Object.keys(fileReportResult.importResult || {}).filter(
                key => fileReportResult.importResult[key]?.section?.some(el => !el.includes("DXXX") && !el.includes("SXXX"))
            ).length;
            const total = Object.keys(fileReportResult.importResult || {}).length;
            const score = total === 0 ? 0 : Math.round(allocated / total * 100);
            dispatch(updateFileAllocation({ fileReport: fileReportResult, currentScore: score, tab: moduleType, autoFillContext: { ...autoFillContext } }));
            dispatch(toggleModal({ open: 'tempAutoFillStepper', autoFillStepperPhase: 'allocation', autoFillStatusBarVisible: false, autoFillStatusBarPhase: 'allocated' }));
            showMessage(uploadFilesRes.message || 'Files allocated - tap to review');
        } catch (error) {
            console.error('Upload error:', error);
            dispatch(toggleModal({ open: '' }));
            showMessage('Upload failed. Please try again.');
            e.target.value = '';
        }
    };

    if (!isOpen) return null;

    const showCancelMinimize = !['select', 'allocation'].includes(phase);

    return (
        <Modal open={isOpen} onClose={() => { }} className="backdrop td500 zi4 pf t0 l0 w100vw h100vh df jcc aic">
            <>
                <div className="pr bg1 shadow w100 bsbb df fdc m1rem br5px" style={{ maxWidth: '1350px', maxHeight: '92vh', overflowY: 'auto', padding: '0 2rem 2rem' }}>

                    {/* Header */}
                    <div style={{ position: 'sticky', top: 0, background: '#fff', zIndex: 2, paddingTop: '1.5rem', paddingBottom: '0.5rem' }}>
                        <div className="df aic jcsb mb0-5rem">
                            <div className="df aic" style={{ gap: '0.5rem' }}>
                                <img src={require('../../../../images/dataRepo/file_placement.svg')} alt="import" style={{ width: '28px', height: '28px' }} />
                                <h2 className="c15 fw600 fs18px mb0rem">Import Files</h2>
                            </div>
                            {phase === 'allocation' && (
                                <button onClick={handleCancel} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6B7280' }}>✕</button>
                            )}
                        </div>
                        <AutoFillStepper currentStep={phase} uploadOnly={true} />
                        <Divider style={{ marginTop: '8px' }} />
                    </div>

                    {/* ── Step 1: Select Files ── */}
                    {phase === 'select' && (
                        <div className="df fdc aic jcc" style={{ padding: '3rem 1rem', gap: '1.5rem', minHeight: '450px' }}>
                            <div style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', borderRadius: '50%', width: '64px', height: '64px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
                                    <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z" fill="white" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                            </div>
                            <div className="tac">
                                <h3 className="c15 fw600 fs18px mb0-5rem">
                                    {modal.autoFillUploadMode === 'folders' ? 'Select folders to import' : 'Select files to import'}
                                </h3>
                                {modal.autoFillUploadMode !== 'folders' && <p className="fs12px" style={{ color: '#9CA3AF' }}>Supported: PDF, DOC, DOCX, XLS, XLSX, PPT, PPTX, TXT</p>}
                            </div>
                            <div className="df aic jcc" style={{ gap: '1rem', marginTop: '0.5rem' }}>
                                <button
                                    onClick={handleCancel}
                                    style={{ background: 'none', border: '1.5px solid #E5E7EB', borderRadius: '8px', cursor: 'pointer', color: '#6B7280', fontSize: '14px', fontWeight: 500, padding: '0.6rem 1.8rem' }}
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={() => fileInputRef.current?.click()}
                                    style={{ background: '#fc7785', border: 'none', borderRadius: '8px', cursor: 'pointer', color: '#fff', fontSize: '14px', fontWeight: 600, padding: '0.6rem 1.8rem' }}
                                >
                                    {modal.autoFillUploadMode === 'folders' ? 'Select Folders' : 'Upload Documents'}
                                </button>
                            </div>
                            {modal.autoFillUploadMode === 'folders'
                                ? <input ref={fileInputRef} type="file" multiple onChange={handleFileSelect} style={{ display: 'none' }} webkitdirectory="" directory="" mozdirectory="" />
                                : <input ref={fileInputRef} type="file" multiple onChange={handleFileSelect} style={{ display: 'none' }} accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt" />
                            }
                        </div>
                    )}

                    {/* ── Step 2: Files Uploading ── */}
                    {phase === 'uploading' && (
                        <div className="df fdc aic jcc" style={{ padding: '1rem 0' }}>
                            <img src={require('../../../../images/loaders/pc-to-data-clarence.gif')} alt="uploading" style={{ maxHeight: '180px' }} />
                            <div className="df jcsb" style={{ width: '97%' }}>
                                <p className="fs12px c43">Your Computer</p>
                                <p className="fs12px c43">Clarence</p>
                            </div>
                            <div className="maw70 w700px df fdc jcc aic mt0rem">
                                <h1 className="c15 fs24px w100 tac fw500">Uploading Files</h1>
                                <div className="w100 df jcc aic mt1rem">
                                    <progress color="green" className="maw90 w90 mr1rem" style={{ border: 'none' }} id="temp-upload-progress-bar" value={modal.percent || 0} max="100" />
                                </div>
                                {modal.fileName && (
                                    <div className="df aic jcc">
                                        <span className="c15 fw500 fs13px my0-5rem">{modal.fileName} | {modal.filesUploaded} / {modal.totalFiles}</span>
                                    </div>
                                )}
                                <p className="c15 fs1rem w100 tac fw400 my2rem">Almost there</p>
                                <div className="df aic jcc" style={{ gap: '1rem' }}>
                                    <button onClick={handleCancel} style={btnCancel}>Cancel</button>
                                    <button onClick={handleMinimize} style={btnMinimize}>Minimize</button>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* ── Step 3: Indexing & Analyzing ── */}
                    {phase === 'indexing' && (
                        <div className="df fdc aic" style={{ padding: '1rem 0' }}>
                            <div className="AnimateWiz tac">
                                <img className="h15rem mla mra" src={require('../../../../images/wizard.gif')} alt="animWiz" />
                            </div>
                            {allocationProgress.total > 0 ? (
                                <>
                                    <h2 className="fs1-5rem-sm w100 tac mb1rem c15">Allocating Files to Sections...</h2>
                                    <div style={{ width: '100%', maxWidth: '520px', margin: '0 auto', textAlign: 'center' }}>
                                        <p className="fs0-85rem c27 mb0-5rem">{allocationProgress.current} of {allocationProgress.total} files matched</p>
                                        {allocationProgress.fileName && <p className="fs0-85rem c27" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', margin: '0 0 0.25rem' }}>File: <span className="c15 fw500">{allocationProgress.fileName}</span></p>}
                                        {allocationProgress.matchedQNo && allocationProgress.matchedQNo !== 'SXXX' && allocationProgress.matchedQNo !== 'DXXX' ? (
                                            <p className="fs0-85rem" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', margin: 0, color: '#fc7785' }}>→ {allocationProgress.matchedQNo} — {allocationProgress.matchedQuestion}</p>
                                        ) : allocationProgress.matchedQNo && <p className="fs0-85rem c27" style={{ margin: 0 }}>→ Unallocated</p>}
                                    </div>
                                </>
                            ) : (
                                <>
                                    <h2 className="fs1-5rem-sm w100 tac mb1rem c15">Indexing & Analysing Files...</h2>
                                    <div style={{ width: '100%', maxWidth: '520px', margin: '0 auto' }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.35rem' }}>
                                            <span className="fs0-75rem c27" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '80%' }}>
                                                {indexingProgress.currentFile ? (indexingProgress.current < indexingProgress.total ? `Reading: ${indexingProgress.currentFile}` : '✓ All files indexed') : 'Preparing files...'}
                                            </span>
                                            <span className="fs0-75rem c27">{indexingProgress.total > 0 ? `${indexingProgress.current || 0}/${indexingProgress.total}` : ''}</span>
                                        </div>
                                        <div style={{ background: '#E5E7EB', borderRadius: '10px', height: '8px', overflow: 'hidden' }}>
                                            <div style={{ background: '#22c55e', height: '100%', borderRadius: '10px', width: indexingProgress.total > 0 ? `${Math.round(((indexingProgress.current || 0) / indexingProgress.total) * 100)}%` : '0%', transition: 'width 0.3s ease' }} />
                                        </div>
                                    </div>
                                </>
                            )}
                            <p className="fs0-75rem c27 tac mt1rem" style={{ opacity: 0.7 }}>You can minimise this window — processing will continue in the background.</p>
                            <div className="df aic jcc" style={{ gap: '1rem', marginTop: '0.75rem' }}>
                                <button onClick={handleCancel} style={btnCancel}>Cancel</button>
                                <button onClick={handleMinimize} style={btnMinimize}>Minimize</button>
                            </div>
                        </div>
                    )}

                    {/* ── Step 4: Allocation ── */}
                    {phase === 'allocation' && fileReport && (
                        <div className="df" style={{ gap: '1.5rem', marginTop: '1rem' }}>
                            <div style={{ flex: previewFile ? '0 0 50%' : '1 1 100%', minWidth: 0, transition: 'flex 0.3s ease' }}>
                                <div className="df jcsb aic mb1rem">
                                    <div className="df jcfs aic">
                                        <img src={require('../../../../images/dataRepo/file_placement.svg')} alt="fileManagement" />
                                        <h1 className="c15 fw600 fs18px mb0rem ml0-5rem">Import Assistant</h1>
                                    </div>
                                    <button onClick={() => onClickFileImportAuditLog(fileReport['_id'])} className="cp tac c1 bg5 c15 br20px px1-5rem py0-5rem fw500 fs13px">Log</button>
                                </div>
                                <div className="dg gg0-5rem mb2rem fs1rem mah300px oya">
                                    <table className="fis bcc">
                                        <thead>
                                            <tr>
                                                <th className="tal fs14px fw400 c37" style={{ width: '45%' }}>Files</th>
                                                <th className="tal fs14px fw400 c37" style={{ width: '10%' }}>Section Name</th>
                                                <th className="tal fs14px fw400 c37" style={{ width: '35%' }}>Allocated Section</th>
                                                {fileReport['status'] == 'In Progress' && <th className="fs14px fw400 c37" style={{ width: '10%' }}>Actions</th>}
                                            </tr>
                                        </thead>
                                        {fileReport.importResult && Object.keys(fileReport.importResult).filter(key => fileReport.importResult[key]['section'].some(el => !el.includes("DXXX") && !el.includes("SXXX"))).length > 0 && (
                                            <tbody>
                                                <tr><td><h1 className="c15 fs18px fw500 tal">Allocated files</h1></td></tr>
                                                {Object.keys(fileReport.importResult).filter(key => fileReport.importResult[key]['section'].some(el => !el.includes("DXXX") && !el.includes("SXXX"))).map(getImportResultEntryHTML)}
                                            </tbody>
                                        )}
                                    </table>
                                    {fileReport.importResult && Object.keys(fileReport.importResult).filter(key => fileReport.importResult[key]['section'].some(el => el.includes("DXXX") || el.includes("SXXX"))).length > 0 && (<>
                                        <h1 className="c15 fs18px fw500 tal mt1rem ml1rem">Unallocated Bin: Please reallocate files <span className="c4">*</span></h1>
                                        <table className="fis bcc">
                                            <thead className="dn">
                                                <tr>
                                                    <th className="fis-th w40 bbn"></th>
                                                    <th className="fis-th bbn"></th>
                                                    <th className="fis-th w60 bbn"></th>
                                                    {fileReport['status'] == 'In Progress' && <th className="fis-th fis-th-action bbn"></th>}
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {Object.keys(fileReport.importResult).filter(key => fileReport.importResult[key]['section'].some(el => el.includes("DXXX") || el.includes("SXXX"))).map(getImportResultEntryHTML)}
                                            </tbody>
                                        </table>
                                    </>)}
                                </div>
                                <div className="df w100 fdr jcc">
                                    {fileReport['status'] == 'In Progress' && fileReport.importResult && Object.keys(fileReport.importResult).filter(key => fileReport.importResult[key]['section'].some(el => !el.includes("DXXX") && !el.includes("SXXX"))).length > 0 && (
                                        <button onClick={onClickReallocationComplete} className="bg3 bg4-hover c1 br10px px3rem py0-75rem cp fw500 fs1rem">Confirm Allocation</button>
                                    )}
                                </div>
                                <div>
                                    <div style={{ width: '60%', height: '1px', backgroundColor: '#82828256', opacity: '64%', margin: '24px auto' }}></div>
                                    <div className="df aic jcc">
                                        <Search className="mr0-5rem" />
                                        <p className="fs13px c37 fw400 mr0-5rem">{fileReport.importResult && (<>[{Object.keys(fileReport.importResult).length}] imported files = [{Object.keys(fileReport.importResult).filter(key => fileReport.importResult[key]['section'].some(el => !el.includes("DXXX") && !el.includes("SXXX"))).length}] allocated files + [{Object.keys(fileReport.importResult).filter(key => fileReport.importResult[key]['section'].some(el => el.includes("DXXX") || el.includes("SXXX"))).length}] unallocated bin files</>)}</p>
                                        <p className="fs13px c37 fw400">Allocation accuracy {currentScore}%</p>
                                    </div>
                                    <div className="df aic jcc" style={{ gap: '1rem', marginTop: '0.5rem' }}>
                                        <button onClick={handleCancel} style={btnCancel}>Cancel</button>
                                        <button onClick={handleMinimize} style={btnMinimize}>Minimize</button>
                                    </div>
                                </div>
                            </div>
                            {previewFile && (
                                <div style={{ flex: '0 0 48%', minWidth: 0, display: 'flex', flexDirection: 'column', borderLeft: '1px solid #e0e0e0', paddingLeft: '1rem' }}>
                                    <div className="df jcsb aic" style={{ marginBottom: '0.75rem' }}>
                                        <div className="df aic" style={{ minWidth: 0 }}>
                                            <VisibleIcon style={{ marginRight: '0.5rem', color: '#6B7280', flexShrink: 0 }} />
                                            <h3 className="c15 fw600 fs14px mb0rem" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{previewFile.name}</h3>
                                        </div>
                                        <div className="df aic" style={{ flexShrink: 0, marginLeft: '0.5rem' }}>
                                            <a href={previewFile.url} target="_blank" rel="noopener noreferrer" className="cp mr0-5rem fs12px c36 fw500 tdu">Open</a>
                                            <CloseIcon className="cp" style={{ fontSize: '1rem', color: '#6B7280' }} onClick={() => setPreviewFile(null)} />
                                        </div>
                                    </div>
                                    <div style={{ flex: 1, borderRadius: '8px', overflow: 'hidden', border: '1px solid #e0e0e0', backgroundColor: '#f9fafb', minHeight: '400px' }}>
                                        <iframe src={previewFile.url} title={`Preview: ${previewFile.name}`} style={{ width: '100%', height: '100%', border: 'none', minHeight: '400px' }} />
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    {/* ── Step 5: Saving & Indexing ── */}
                    {phase === 'processing' && (
                        <div className="df fdc aic jcc" style={{ padding: '1rem 0' }}>
                            <div className="pr" style={{ display: 'inline-block' }}>
                                <img src={require('../../../../images/loaders/clarence-to-data-repository.gif')} alt="processing" style={{ maxHeight: '180px', display: 'block' }} />
                                <div className="df jcsb" style={{ width: '100%', marginTop: '0.25rem' }}>
                                    <p className="fs12px c43" style={{ textAlign: 'left' }}>Clarence</p>
                                    <p className="fs12px c43" style={{ textAlign: 'right' }}>Data Repository</p>
                                </div>
                            </div>
                            <div className="maw70 w700px df fdc jcc aic mt0rem">
                                <h1 className="c15 fs24px w100 tac fw500">Saving files and linking them to your answers...</h1>
                                <div className="w100 mt1rem" style={{ maxWidth: '520px' }}>
                                    <div className="df jcsb mb0-25rem">
                                        <span className="fs0-75rem c27" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '75%' }}>
                                            {savingProgress.fileName ? `Saving: ${savingProgress.fileName}` : 'Preparing...'}
                                        </span>
                                        <span className="fs0-75rem c27">{savingProgress.total > 0 ? `${savingProgress.current}/${savingProgress.total}` : ''}</span>
                                    </div>
                                    <div style={{ background: '#E5E7EB', borderRadius: '10px', height: '8px', overflow: 'hidden' }}>
                                        <div style={{ background: '#22c55e', height: '100%', borderRadius: '10px', width: savingProgress.total > 0 ? `${Math.round((savingProgress.current / savingProgress.total) * 100)}%` : '0%', transition: 'width 0.3s ease' }} />
                                    </div>
                                    {savingProgress.qNo && <p className="fs0-75rem tac mt0-5rem" style={{ color: '#4a67ff' }}>→ Linked to: {savingProgress.qNo}</p>}
                                </div>
                                <div className="df aic jcc mt1-5rem" style={{ gap: '1rem' }}>
                                    <button onClick={handleCancel} style={btnCancel}>Cancel</button>
                                    <button onClick={handleMinimize} style={btnMinimize}>Minimize</button>
                                </div>
                            </div>
                        </div>
                    )}

                </div>

                {/* Girl image — allocation step only */}
                {phase === 'allocation' && fileReport && (
                    <div style={{ position: 'relative' }}>
                        {fileReport.importResult && Object.keys(fileReport.importResult).filter(key => fileReport.importResult[key]['section'].some(el => el.includes("DXXX") || el.includes("SXXX"))).length > 0
                            ? <img src={require('../../../../images/dataRepo/sorryMessage.png')} alt="messageLadie" className="animateRight" />
                            : <img src={require('../../../../images/dataRepo/fillingassistantGirl.png')} alt="messageLadie" className="animateRight" />
                        }
                    </div>
                )}
            </>
        </Modal>
    );
};

export default TempAutoFillStepperModal;
